import { useDrag, useWheel } from 'react-use-gesture';
import { useFrame, useThree } from '@react-three/fiber';

import { PerspectiveCamera } from 'three';
import { useRef } from 'react';

interface CameraControllerProps {
  sensitivity?: number | undefined;
  zoomSensitivity?: number | undefined;
  fovRange?: { min: number; max: number } | undefined;
}

const CameraController: React.FC<CameraControllerProps> = ({
  sensitivity = 0.0015,
  zoomSensitivity = 0.04,
  fovRange = { min: 40, max: 90 },
}) => {
  const {
    gl: { domElement: domTarget },
  } = useThree();

  const stateRef = useRef({
    rotation: { x: 0, y: 0 },
    targetFOV: fovRange.max,
  });

  useDrag(
    ({ delta }) => {
      stateRef.current.rotation.x += delta[0];
      stateRef.current.rotation.y += delta[1];
    },
    { domTarget }
  );

  useWheel(
    ({ delta: [_, scrollY] }) => {
      stateRef.current.targetFOV = Math.min(
        fovRange.max,
        Math.max(
          fovRange.min,
          stateRef.current.targetFOV + scrollY * zoomSensitivity
        )
      );
    },
    { domTarget }
  );

  useFrame((state) => {
    const { min: minFOV, max: maxFOV } = fovRange;

    const cam: PerspectiveCamera = state.camera as PerspectiveCamera;

    const rotationScaler = sensitivity * (cam.fov / ((minFOV + maxFOV) / 2));

    const unclamppedRotationY =
      cam.rotation.x + stateRef.current.rotation.y * rotationScaler;
    cam.rotation.x = Math.min(
      Math.PI / 2,
      Math.max(-Math.PI / 2, unclamppedRotationY)
    );
    cam.rotation.y += stateRef.current.rotation.x * rotationScaler;

    cam.fov += (stateRef.current.targetFOV - cam.fov) * 0.2;
    cam.updateProjectionMatrix();
    stateRef.current.rotation.x = 0;
    stateRef.current.rotation.y = 0;
  });

  return <group />;
};
export default CameraController;
